/**
 * Query module.
 * @module src/query
 */
import { elementIs, isVisible } from './element';
/**
 * Returns the element parent for a givent parent level
 *
 * @function getParent
 *
 * @param {HTMLElement} element - the HTML element
 * @param {number} [parentLevel=1] - the number of levels to up to until parent
 * @return {boolean} - if the element has or not true value to this property
 *
 * @example
 * const element = document.createElement('html');
 * getParent(element);
 * // returns null
 *
 * @example
 * const element = document.createElement('html');
 * const childElement = document.createElement('body');
 * getParent(childElement);
 * // returns element
 *
 * @example
 * const html = document.createElement('html');
 * const body = document.createElement('body');
 * const div = document.createElement('div');
 * getParent(div);
 * // returns  body
 * getParent(div, 2);
 * // returns  html
 *
 */

export const getParent = function (element) {
  let parentLevel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;

  while (parentLevel-- > 0) {
    element = element.parentElement;

    if (!element) {
      return null;
    }
  }

  return element;
};
/**
 * Finds a DOM elemnt given a [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp}
 *
 * @function find
 *
 * @param {string} selector - the [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} used to find the element
 * @param {HTMLElement} [parent=document] - the parent element to scope the query
 * @return {HTMLElement|null} - the HTML element found with query selector
 *
 * @example
 * document.body.innerHTML = `<div class="search">
 *                              <input id="query"/>
 *                              <p id="result""/>
 *                            </div>
 *                            <div class="register">
 *                              <input id="name"/>
 *                              <button id="submit"/>
 *                            </div>`;
 * const searchContainer = find('.search');
 * // returns div.search
 * const registerContainer = find('.regisrer');
 * // returns div.search
 * const registerContainer = find('#query', searchContainer);
 * // returns input#query
 * const registerContainer = find('#query', registerContainer);
 * // returns null
 *
 */

export const find = function (selector) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  return parent.querySelector(selector);
};
/**
 * Finds all DOM elemnts given a [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp}
 *
 * @function findAll
 *
 * @param {string} selector - the [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} used to find the element
 * @param {HTMLElement} [parent=document] - the parent element to scope the query
 * @return {HTMLElement[]} - the HTML elements list found with query selector
 *
 * @example
 * document.body.innerHTML = `<ul>
 *                              <li>Maria Luisa<li>
 *                              <li>Alexandre Santos<li>
 *                              <li>Matheus Batista<li>
 *                              <li>Matheus Richard<li>
 *                              <li>Andre Filho<li>
 *                              <li>Guilherme Silva<li>
 *                              <li>Bruno Vicenzo<li>
 *                           </ul>`;
 * const list = find('ul');
 * // returns ul
 * const listItems = findAll('li');
 * // returns [li, li, ...]
 * const registerContainer = find('li.active', list);
 * // returns []
 *
 */

export const findAll = function (selector) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  return Array.from(parent.querySelectorAll(selector));
};
/**
 * Finds a DOM elemnt given a [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} and a conditional function
 *
 * @function findIf
 *
 * @param {string} selector - the [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} used to find the element
 * @param {function} condition - the condition to check if is the asked element
 * @param {HTMLElement} [parent=document] - the parent element to scope the query
 * @return {HTMLElement|null} - the HTML element found with query selector
 *
 * @example
 * import { isDisabled, isHidden } from 'f-js-dom';
 * document.body.innerHTML = `<form class="register">
 *                              <input type=text name="email" disabled="disabled" />
 *                              <input type=password name="password" />
 *                              <button type="submit">Save</button>
 *                            </form>`;
 * const form = find('form.register');
 * // returns form.register
 * findIf('input', isDisabled, form);
 * // returns email input
 * findIf('input', isHidden, form);
 * // returns null
 *
 */

export const findIf = function (selector, condition) {
  let parent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : document;
  const elements = findAll(selector, parent);
  return elements.find(element => condition(element)) || null;
};
/**
 * Finds a DOM elemnt given a [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} if it is visible
 *
 * @function findVisible
 *
 * @param {string} selector - the [CSS Query Selector]{@link https://www.w3schools.com/cssref/css_selectors.asp} used to find the element
 * @param {HTMLElement} [parent=document] - the parent element to scope the query
 * @return {HTMLElement|null} - the HTML element found with query selector
 *
 * @example
 * document.body.innerHTML = `<form class="register">
 *                              <input type=text name="email" style="display:none;" />
 *                              <input type=password name="password" />
 *                              <button type="submit" style="display:none;>Save</button>
 *                            </form>`;
 * const form = find('form.register');
 * // returns form.register
 * findVisible('input', form);
 * // returns password input
 * findVisible('button', form);
 * // returns null
 *
 */

export const findVisible = function (selector) {
  let parent = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  return findIf(selector, isVisible, parent);
};
/**
 * Gets the element value if it is enabled
 *
 * @function elementValueIfEnabled
 *
 * @param {HTMLElement} element - the HTML element to have its value gotten
 * @return {string|null} - the element value
 *
 * @example
 * document.body.innerHTML = `<form class="register">
 *                              <input type=text name="email" value="name@fretadao.com.br" />
 *                              <input type=password name="password" value="123456" disabled="disabled" />
 *                              <button type="submit" style="display:none;>Save</button>
 *                            </form>`;
 * const form = find('form.register');
 * // returns form.register
 * const emailField = findVisible('input[name=email]', form);
 * const passwordField = findVisible('input[type=password]', form);
 * elementValueIfEnabled(emailField);
 * // returns name@fretadao.com.br
 * elementValueIfEnabled(passwordField);
 * // returns null
 *
 */

export const elementValueIfEnabled = element => !elementIs('disabled', element) ? element.value : null;